import { useState, type FormEvent } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import { useTranslation } from "react-i18next";
import { services } from "@/injectServices";
import { defaultLanguage, isLanguage, isValidEmail } from "@/models";
import { setPerfumeProperty, type PerfumeEditor } from "@/services/perfume-editor";

// end of the tunnel: the client receives their formula by email, at the address they entered
// (editable here). See docs/send-perfume-by-email.md
export function SendPerfumeByEmail({
  editor,
  recipeId,
}: {
  editor: PerfumeEditor;
  recipeId: string;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const handleOpen = () => {
    setEmail(editor.perfumeViewModel.email);
    setError(null);
    setOpen(true);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const to = email.trim();
    if (!isValidEmail(to)) {
      setError(t("sendByEmailInvalid"));
      return;
    }
    // a corrected address is kept on the formula (autosaved like any edit)
    if (to !== editor.perfumeViewModel.email) {
      setPerfumeProperty(editor, { newEmail: to });
    }
    setSending(true);
    try {
      const language = isLanguage(i18n.resolvedLanguage) ? i18n.resolvedLanguage : defaultLanguage;
      await services().repositories.perfumeEmailRepository.sendPerfumeByEmail({
        recipeId,
        email: to,
        language,
      });
      setOpen(false);
      setSentTo(to);
    } catch (sendError) {
      console.error("Can't send perfume by email", sendError);
      const code = (sendError as { code?: string }).code;
      setError(
        code === "functions/resource-exhausted"
          ? t("sendByEmailTooMany")
          : code === "functions/invalid-argument"
            ? t("sendByEmailInvalid")
            : t("sendByEmailError"),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button variant="contained" startIcon={<EmailOutlined />} onClick={handleOpen} sx={{ mt: 3 }}>
        {t("sendByEmail")}
      </Button>
      {sentTo !== null && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {t("sendByEmailSent", { email: sentTo })}
        </Alert>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <form onSubmit={handleSubmit} noValidate>
          <DialogTitle>{t("sendByEmailTitle")}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("sendByEmailText")}</DialogContentText>
            <TextField
              autoFocus
              fullWidth
              type="email"
              label="Email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              slotProps={{
                htmlInput: { inputMode: "email", autoCapitalize: "none", autoCorrect: "off" },
              }}
              error={error !== null}
              helperText={error ?? " "}
              variant="standard"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button type="submit" variant="contained" disabled={sending || email.trim() === ""}>
              {t("sendByEmailSend")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
